import type {
  LastRunTelemetryEvent,
  LastRunTelemetrySummary,
} from '@/types/api';

export type PipelineStepStatus = 'not_started' | 'in_progress' | 'completed' | 'failed';

export interface PipelineStep {
  key: string;
  label: string;
  status: PipelineStepStatus;
}

export interface SimplifiedTimelineItem {
  id: string;
  timestamp: string;
  label: string;
  isEnd: boolean;
}

/**
 * Ordered list of core pipeline steps for the run detail & summary views.
 * This mirrors the legacy dashboard/telemetry UI, including the initial
 * PDF ingestion step that happens before image extraction.
 */
const CORE_STEPS: PipelineStep[] = [
  { key: 'pdf_ingestion', label: 'PDF Ingestion', status: 'not_started' },
  { key: 'extraction_images', label: 'Extraction (Images PNG)', status: 'not_started' },
  { key: 'chapter_detector', label: 'Chapter Detection', status: 'not_started' },
  { key: 'synthesize_tts', label: 'Synthesize TTS', status: 'not_started' },
  { key: 'assemble_audio', label: 'Assemble', status: 'not_started' },
  { key: 'generate_credits', label: 'Credits', status: 'not_started' },
  { key: 'qa_finalize', label: 'QA', status: 'not_started' },
];

const ALIAS_MAP: Record<string, string[]> = {
  // High‑level pipeline stages reported in summary
  pdf_ingestion: ['pdf_ingestion'],
  extract_structure: ['extract_structure', 'structure_extract', 'extractor', 'structure_extraction', 'extract'],
  synthesize_tts: ['synthesize_tts', 'tts_synthesis', 'speech_synthesis', 'synthesize', 'tts'],
  // Align with Telemetry summary keys for images extraction and chapter detection
  extraction_images: ['extraction_images', 'image_processor_png', 'image_processing', 'extract_images'],
  chapter_detector: ['chapter_detector', 'chapter_detection'],
  assemble_audio: ['assemble_audio', 'merge_audio', 'concat_audio', 'concatenate_audio', 'assemble'],
  generate_credits: ['generate_credits', 'credits_generation', 'credits'],
  qa_finalize: ['qa_finalize', 'qa_final', 'quality_assurance', 'qa', 'finalize'],
};

const EVENT_PATTERNS: Record<string, string[]> = {
  pdf_ingestion: ['pdf_ingestion', 'pdf'],
  extract_structure: ['extract_structure', 'structure_extract', 'extractor', 'structure_extraction', 'extract'],
  synthesize_tts: ['synthesize_tts', 'tts_synthesis', 'speech_synthesis', 'synthesize', 'tts'],
  extraction_images: ['image_processor_png', 'image_processing', 'extraction_images', 'extract_images'],
  chapter_detector: ['chapter_detector', 'chapter_detection'],
  assemble_audio: ['assemble_audio', 'merge_audio', 'concat_audio', 'concatenate_audio', 'assemble'],
  generate_credits: ['generate_credits', 'credits_generation', 'credits'],
  qa_finalize: ['qa_finalize', 'qa_final', 'quality_assurance', 'qa', 'finalize'],
};

function normalizeStepStatus(obj: unknown): PipelineStepStatus {
  if (!obj || typeof obj !== 'object') return 'not_started';
  const anyObj = obj as Record<string, unknown>;
  const raw = typeof anyObj.status === 'string' ? anyObj.status.toLowerCase() : '';

  if (raw) {
    if (raw === 'in progress' || raw === 'in_progress') return 'in_progress';
    if (['done', 'success', 'finished', 'complete', 'completed'].includes(raw)) return 'completed';
    if (['failed', 'error'].includes(raw)) return 'failed';
  }

  // Derive from timestamps/flags when status field is missing or unknown
  if (anyObj.completed || anyObj.completed_at) return 'completed';
  if (anyObj.started || anyObj.started_at) return 'in_progress';
  return 'not_started';
}

function findStatusInSummary(
  key: string,
  summary: LastRunTelemetrySummary | undefined
): PipelineStepStatus | null {
  if (!summary) return null;
  const aliases = ALIAS_MAP[key] || [key];
  for (const alias of aliases) {
    if (Object.prototype.hasOwnProperty.call(summary, alias)) {
      const value = (summary as Record<string, unknown>)[alias];
      return normalizeStepStatus(value);
    }
  }
  return null;
}

function deriveStatusFromEvents(
  key: string,
  events: LastRunTelemetryEvent[] | undefined
): PipelineStepStatus | null {
  if (!events || events.length === 0) return null;
  const patterns = (EVENT_PATTERNS[key] || [key]).map((s) => s.toLowerCase());

  const hasPattern = (event: LastRunTelemetryEvent, suffix: string) => {
    const name = (event.event_name || '').toLowerCase();
    return patterns.some((p) => name.includes(`${p}_${suffix}`));
  };

  const hasEnd = events.find((e) => hasPattern(e, 'end'));
  const hasStart = events.find((e) => hasPattern(e, 'start'));

  if (hasEnd) return 'completed';
  if (hasStart) return 'in_progress';
  return null;
}

function resolveStepStatus(
  key: string,
  summary: LastRunTelemetrySummary | undefined,
  events: LastRunTelemetryEvent[] | undefined
): PipelineStepStatus {
  return (
    findStatusInSummary(key, summary) ??
    deriveStatusFromEvents(key, events) ??
    'not_started'
  );
}

export function buildPipelineSteps(
  summary: LastRunTelemetrySummary | undefined,
  events: LastRunTelemetryEvent[] | undefined,
  runStatus?: string
): PipelineStep[] {
  const normalizedRunStatus = String(runStatus || '').toLowerCase();
  const hasQaFinalizeEnd = Boolean(
    events?.some((e) => (e.event_name || '').toLowerCase() === 'qa_finalize_end')
  );
  const qaSummaryStatus = (() => {
    if (!summary || typeof summary !== 'object') return '';
    const qa = (summary as Record<string, unknown>).qa_finalize as
      | { status?: unknown; completed?: unknown; completed_at?: unknown }
      | undefined;
    if (!qa) return '';
    const raw = String(qa.status || '').toLowerCase();
    if (raw) return raw;
    return qa.completed || qa.completed_at ? 'completed' : '';
  })();

  const isTerminalCompleted =
    ['completed', 'success', 'done', 'finished', 'complete'].includes(normalizedRunStatus) ||
    ['completed', 'success', 'done', 'finished', 'complete'].includes(qaSummaryStatus) ||
    hasQaFinalizeEnd;

  if (isTerminalCompleted) {
    return CORE_STEPS.map((step) => ({
      ...step,
      status: 'completed',
    }));
  }

  return CORE_STEPS.map((step) => ({
    ...step,
    status: resolveStepStatus(step.key, summary, events),
  }));
}

export function getPipelineStatusDisplay(steps: PipelineStep[]): string {
  const inProgress = steps.find((s) => s.status === 'in_progress');
  if (inProgress) {
    return `In Progress: ${inProgress.label}`;
  }

  const next = steps.find((s) => s.status === 'not_started');
  if (next) {
    return `Next: ${next.label}`;
  }

  const failed = steps.find((s) => s.status === 'failed');
  if (failed) {
    return `Failed: ${failed.label}`;
  }

  return 'Pipeline Completed';
}

export function buildSimplifiedTimeline(
  events: LastRunTelemetryEvent[] | undefined,
  maxItems = 5
): SimplifiedTimelineItem[] {
  if (!events || events.length === 0) return [];

  const keyEvents = events.filter((e) =>
    (e.event_name || '').includes('_start') || (e.event_name || '').includes('_end')
  );

  const sorted = [...keyEvents].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  const slice = sorted.slice(-maxItems);

  return slice.map((e, idx) => ({
    id: `${idx}-${e.timestamp}-${e.event_name}`,
    timestamp: e.timestamp,
    label: e.event_name.replace(/_/g, ' '),
    isEnd: e.event_name.includes('_end'),
  }));
}


